import React, { useEffect } from "react";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import { searchPlugin } from "@react-pdf-viewer/search";
import { toolbarPlugin } from "@react-pdf-viewer/toolbar";
import { zoomPlugin } from "@react-pdf-viewer/zoom";
import styled, { ThemeProvider } from "styled-components";
import { useState } from "react";
import { useSelector } from "react-redux";
import "../../styles/StylesPdfViewer.css";
import { myCandidatesSelector } from "../redux/selector";
import Key from "../../../clientVariables.json";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { renderAsync } from "docx-preview";
import { useRef } from "react";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import SearchIcon from "@mui/icons-material/Search";
import { InputBase } from "@mui/material";

// Define your custom theme
const customTheme = {
  viewer: {
    backgroundColor: "#fff", // Change the background color of the viewer
  },
  toolbar: {
    backgroundColor: "#f5f5f5",
    color: "#333 !important",
  },
  // You can add more customizations here for different parts of the layout
};

// Style the Viewer component using styled-components
const StyledViewer = styled(Viewer)`
  background-color: ${({ theme }) => theme.viewer.backgroundColor};
`;

const DocxViewerContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
  overflow: hidden;

  iframe {
    width: 100%;
    height: 100%;
    border: none;
    flex: 1;
  }
`;

const DocxToolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #ffffff;
  color: #333333;
  z-index: 1000;
  min-height: 48px;
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
`;

const DocxContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 20px;
  background-color: #ffffff;
  
  /* Custom selection color for search highlighting */
  & ::selection {
    background: #ffff00 !important;
    color: #000 !important;
  }

  .docx-wrapper {
    background-color: #ffffff !important;
    padding: 0 !important;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
  
  article.docx {
    background-color: #ffffff !important;
    box-shadow: 0 0 10px rgba(0,0,0,0.1) !important;
    margin-bottom: 30px !important;
    padding: 50px !important;
    width: 850px !important;
    min-height: 1000px !important;
    color: #000000 !important;
  }
`;

const ResumePdfViewer = ({ resume }) => {
  // Initialize the plugins
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    sidebarTabs: (defaultTabs) => [],
  });
  const searchPluginInstance = searchPlugin();
  const toolbarPluginInstance = toolbarPlugin();
  const zoomPluginInstance = zoomPlugin();
  const [defaultFile, setDefaultFile] = useState();
  const [fileType, setFileType] = useState("");
  const { candidateDetails } = useSelector(myCandidatesSelector);
  const [zoom, setZoom] = useState(1.0);
  const [searchTerm, setSearchTerm] = useState("");
  const docxRef = useRef(null);

  useEffect(() => {
    if (resume?.file_name) {
      const extension = resume.file_name.split(".").pop().toLowerCase();
      setFileType(extension);
      setSearchTerm(""); // Clear search when switching resume
      const fileUrl = `${Key.domain}/candidate/get-resume/root/data/bench_data/attachments/cand_${candidateDetails?.candidate_id}/${resume.file_name}`;
      setDefaultFile(fileUrl);
    }
  }, [resume, candidateDetails]);

  useEffect(() => {
    if (fileType === "docx" && defaultFile && docxRef.current) {
      fetch(defaultFile)
        .then((response) => response.arrayBuffer())
        .then((buffer) => {
          docxRef.current.innerHTML = "";
          renderAsync(buffer, docxRef.current, null, {
            className: "docx",
            inWrapper: true,
            ignoreLastRenderedPageBreak: false,
          });
        })
        .then(() => {
          console.log("ResumeSelection ResumePdfViewer - DOCX render completed successfully");
          applyZoom();
        })
        .catch((error) => console.error("Error rendering docx:", error));
    }
  }, [fileType, defaultFile]);

  const applyZoom = () => {
    if (docxRef.current) {
      const wrapper = docxRef.current.querySelector('.docx-wrapper');
      if (wrapper) {
        wrapper.style.zoom = zoom;
      } else {
        docxRef.current.style.zoom = zoom;
      }
    }
  };

  useEffect(() => {
    applyZoom();
  }, [zoom]);

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = defaultFile;
    link.download = resume?.file_name || "document.docx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));

  const handleSearch = (e) => {
    if (e.key === 'Enter' && searchTerm) {
      window.find(searchTerm, false, false, true);
    }
  };

  const executeSearch = () => {
    if (searchTerm) {
      window.find(searchTerm, false, false, true);
    }
  };

  const renderViewer = () => {
    if (!defaultFile) return <div>Loading...</div>;

    if (fileType === "pdf") {
      return (
        <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
          <StyledViewer
            fileUrl={defaultFile}
            plugins={[
              defaultLayoutPluginInstance,
              searchPluginInstance,
              toolbarPluginInstance,
              zoomPluginInstance,
            ]}
          />
        </Worker>
      );
    } else if (fileType === "docx") {
      return (
        <DocxViewerContainer>
          <DocxToolbar>
            {/* Left: Search */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                bgcolor: '#f1f3f4', 
                borderRadius: 1, 
                px: 1,
                width: 200
              }}>
                <SearchIcon fontSize="small" sx={{ color: "#5f6368", mr: 1 }} />
                <InputBase
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handleSearch}
                  sx={{ color: '#3c4043', fontSize: '0.875rem', width: '100%' }}
                />
                <IconButton onClick={executeSearch} size="small" sx={{ color: "#5f6368" }}>
                  <SearchIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Middle: Zoom */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#3c4043", minWidth: 50, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: Download */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Download">
                <IconButton onClick={handleDownload} size="small" sx={{ 
                  color: "#5f6368",
                  '&:hover': { bgcolor: '#f1f3f4' }
                }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DocxToolbar>
          <DocxContent ref={docxRef} />
        </DocxViewerContainer>
      );
    } else if (fileType === "doc") {
      // Fallback for legacy .doc files with consistent toolbar (Search hidden as not supported)
      return (
        <DocxViewerContainer>
          <DocxToolbar>
            {/* Left: Empty (Search not supported for .doc) */}
            <Box sx={{ flex: 1 }} />

            {/* Middle: Zoom (CSS Scale for .doc iframe) */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1
              }}>
                <Tooltip title="Zoom Out">
                  <IconButton onClick={handleZoomOut} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <RemoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" sx={{ color: "#3c4043", minWidth: 50, textAlign: 'center', fontWeight: 500 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
                <Tooltip title="Zoom In">
                  <IconButton onClick={handleZoomIn} size="small" sx={{ color: "#5f6368", border: '1px solid #dadce0' }}>
                    <AddIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Right: Download */}
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Tooltip title="Download">
                <IconButton onClick={handleDownload} size="small" sx={{ 
                  color: "#5f6368",
                  '&:hover': { bgcolor: '#f1f3f4' }
                }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </DocxToolbar>
          <Box sx={{ 
            flex: 1, 
            width: '100%', 
            overflow: 'auto', 
            display: 'flex', 
            justifyContent: 'center',
            bgcolor: '#f0f2f5',
            p: 2
          }}>
            <Box sx={{
              width: '850px',
              height: 'fit-content',
              minHeight: '100%',
              transform: `scale(${zoom})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease-in-out',
              boxShadow: '0 0 10px rgba(0,0,0,0.1)'
            }}>
              <iframe
                src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(defaultFile)}`}
                title="Document Viewer"
                style={{ width: '100%', height: '1000px', border: 'none' }}
              />
            </Box>
          </Box>
        </DocxViewerContainer>
      );
    } else {
      return (
        <div>
          Unsupported file type.{" "}
          <a href={defaultFile} target="_blank" rel="noopener noreferrer">
            Download file
          </a>
        </div>
      );
    }
  };

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', overflow: 'hidden' }}>
        {renderViewer()}
      </Box>
    </ThemeProvider>
  );
};

export default ResumePdfViewer;
